import React from 'react';
import PropTypes from 'prop-types';
import dotProp from 'dot-prop';
import {Segment} from 'semantic-ui-react';
import {clone, EmptyFunc, isFalsy, isNoU, isTruthy, TopLevelSectionName} from "utils/DBUtils";
import SegmentSectionWrapper from 'components/common/SegmentSectionWrapper';
import ConditionalSection from "components/common/ConditionalSection";
import BasicForm from 'components/forms/BasicForm';
import ResourceFormProxy from "classes/ResourceFormProxy";
import ViewSwitchSection from 'components/forms/ViewSwitchSection';
import CollectionManagementSection from 'components/forms/CollectionManagementSection';
import {isFunction} from "../../utils/DBUtils";

// this emulates React.Fragment but won't complain if we pass props to it
const SectionFragment = ({ children }) => {
  return children;
};

/**
 * Component used to render config based forms
 */
class ResourceForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            rendered: false
        };

        if (props.staticRender) {
            this.sections = this.generateSections();
        }
    }

    generateSections() {
        let config = this.props.config;

        // If fields property given wrap config in a single element array as sections
        // Normalizes rest of the generation
        if (isFalsy(config.sections) && isTruthy(config.fields)) {
            config = {
                sections: [config]
            };
        }

        let sectionCount = 0;
        const sections = config.sections.map((section) => {
            let {sectionComp, sectionsCreated} = this.makeSection(section, sectionCount);
            sectionCount += (sectionsCreated || 0);
            return sectionComp;
        });

        return this.props.sectionWrapper === SegmentSectionWrapper ? (
            <Segment.Group>{sections}</Segment.Group>
        ) : sections;
    }

    /**
     * Factory method for creating a proxy object
     * @param fieldPath
     * @param resourcePath
     * @return {ResourceFormProxy}
     */
    makeProxy(fieldPath, resourcePath) {
        return new ResourceFormProxy(this, clone(fieldPath), clone(resourcePath));
    }

    /**
     * Contains logic to get the dynamic props of fields.
     * Can be overridden to inject props that should be on all fields
     * @see MultiPlatformResourceForm
     * @param field
     * @param fieldPath
     * @return {{}}
     */
    fieldProps(field, fieldPath) {
        return this.props.fieldProps(field, fieldPath) || {};
    }

    /**
     * Returns the value of given field.
     * @param fieldPath
     * @return {*}
     */
    valueForField(fieldPath) {
        return dotProp.get(this.props.resource, fieldPath.join('.'));
    }

    /**
     * Returns the current value of a given field.
     * @see MultiPlatformResourceForm
     * @param fieldPath
     * @return {*}
     */
    currentValueForField(fieldPath) {
        return this.valueForField(fieldPath);
    }

    /**
     * Returns the value of given field.
     * @param resourcePath
     * @return {*}
     */
    resourceAtPath(resourcePath) {
        if (!resourcePath || resourcePath.length === 0) {
            return this.props.resource
        }
        return dotProp.get(this.props.resource, resourcePath.join('.'));
    }

    /**
     * Returns the current value of a given field.
     * @see MultiPlatformResourceForm
     * @param resourcePath
     * @return {*}
     */
    currentResourceAtPath(resourcePath) {
        return this.resourceAtPath(resourcePath);
    }

    /**
     * Factory method for creating onValueChanged callback that gets passed to each field component.
     * Can be overridden to provide additional parameters or different injected parameters
     * @param fieldPath
     * @param resourcePath
     * @return {function(*=)}
     */
    makeOnValueChanged(fieldPath, resourcePath) {
        return (value) => this.props.onValueChanged(resourcePath, fieldPath, value);
    }

    makeOnValidationError(fieldPath, resourcePath) {
        if (this.props.onValidationError) {
            return (value, errors) => this.props.onValidationError(resourcePath, fieldPath, value, errors);
        }
        return EmptyFunc;
    }

    /**
     * Creates a unique key for a component field base on the field and resource.
     * Used by React for rendering.
     * @param field
     * @param resourcePath
     * @return {string}
     */
    getUniqueComponentKey(field, resourcePath) {
        if (field.key) {
            return field.key
        }
        
        let key = resourcePath.join("/");

        if (field.name) {
            key += field.name;
        }

        if (field.type) {
            key += field.type;
        }

        let resource = this.currentResourceAtPath(resourcePath);
        if (resource && resource.resourceId) {
            key += resource.resourceId.id;
        }
        return key;
    }

    /**
     * Renders a component based on the given config 'field'
     * @param field
     * @param fieldPath
     * @param resourcePath
     * @param sectionIndex
     * @param sectionKey
     * @return {*}
     */
    makeComponent(field, fieldPath, resourcePath, sectionIndex, sectionKey) {
        let Component = this.props.components[field.type];
        if (!Component) {
            // Can't render without a component
            console.warn(`Could not find component type ${field.type} in config`);
            console.warn(field);
            console.warn(this.props.components);
            return "";
        }

        // Having the path override occur here means that it can't affect subsections
        // If this is needed in future, then this logic will need to be in makeSectionFields

        // Gives config option to rewrite the paths used for this component
        if (isFunction(field.pathOverride)) {
            // get new paths, slicing before to prevent accidental modification
            let newPaths = field.pathOverride(fieldPath.slice(), resourcePath.slice());
            fieldPath = newPaths.fieldPath;
            resourcePath = newPaths.resourcePath
        }

        // Get dynamic props for field
        let fieldProps = this.fieldProps(field, fieldPath);

        let showField = true;
        if (isFunction(field.showField)) {
            showField = field.showField(this.makeProxy(fieldPath, resourcePath))
        }

        // return showField ? 
        return <Component {...field.props}                          
                          {...fieldProps}
                          key={this.getUniqueComponentKey(field, resourcePath)}
                          fieldKey={this.getUniqueComponentKey(field, resourcePath)}
                          name={field.name}
                          showField={showField}
                          errors={this.props.errors[field.name]}
                          disabled={isFunction(field.disabled) ? field.disabled(this.makeProxy(fieldPath, resourcePath)) : Boolean(field.disabled)}
                          required={typeof(field.required) === typeof(true) || typeof(field.required) === 'undefined' ? field.required : field.required(this.makeProxy(fieldPath, resourcePath))}
                          sectionIndex={sectionIndex}
                          value={this.valueForField(fieldPath)}
                          onValueChanged={this.makeOnValueChanged(fieldPath, resourcePath)}
                          onValidationError={this.makeOnValidationError(fieldPath, resourcePath)}
                          formProxy={this.makeProxy(fieldPath, resourcePath)}/>;
            // :
            // null
            // ;
    }

    /**
     * Render all fields for a section
     * Is recursive in the case that the section has sub sections
     * @param section
     * @param baseFieldPath
     * @param baseResourcePath
     * @param sectionIndex
     * @param sectionKeyPath
     * @return {Array}
     */
    makeSectionFields(section, baseFieldPath = [], baseResourcePath = [], sectionIndex, sectionKeyPath) {
        let fieldComponents = [];

        if (section.hideIfResourceIsNoU) {
            if (isNoU(this.currentResourceAtPath(baseResourcePath))) {
                return fieldComponents;
            }
        }

        // This blocks connected showSection in multiplatform resource form from being re-rendered because the children are never created
        // Not used by any other form right now, but should be supported eventually
        // if (isFunction(section.showSection) && !section.showSection(this.makeProxy(baseFieldPath, baseResourcePath))) {
        //     return fieldComponents;
        // }

        let fields = section.fields;

        if (!isNoU(section.viewSwitch)) {
            if (isNoU(section.viewCases)) {
                console.warn(`'viewSwitch' option defined without 'viewCases' option in section ${section.key || section.name || section}`);
            } else {
                let viewCaseFields = {};

                let fieldPath = baseFieldPath.slice();
                let resourcePath = baseResourcePath.slice();

                let viewCases = isFunction(section.viewCases)
                    ? section.viewCases(this.makeProxy(fieldPath, resourcePath))
                    : section.viewCases;

                for (let key of Object.keys(viewCases)) {
                    // Make artificial section for each case
                    let caseSection = {fields: viewCases[key]};
                    viewCaseFields[key] = this.makeSectionFields(caseSection, fieldPath, resourcePath, sectionIndex, sectionKeyPath);
                }

                return [
                    this.makeViewSwitchSectionWrapper(section, viewCaseFields, baseFieldPath, baseResourcePath)
                ];
            }
        }

        if (isNoU(fields)) {
            console.warn(`No fields provided for section ${section.key || section.name}`, section);
            return fieldComponents;
        }

        let fieldCount = 0;
        for (let field of fields) {

            // Create current field and resource paths for this field

            // Clone for safety
            // Only append new path if exists, one case is for subsections that affect same root object
            let fieldPath = baseFieldPath.slice();
            if (!isNoU(field.name)) {
                fieldPath.push(field.name);
            }

            let resourcePath = baseResourcePath.slice();
            if (!isNoU(field.resource) && !isNoU(field.name)) {
                resourcePath = resourcePath.concat(field.name);
            }

            let currentValue = this.currentValueForField(fieldPath);
            if (field.subSection === true) {

                // TODO: Go through configs and remove behavior where equals 'sectionPerElement' with collection === true second check
                // existing 'sectionPerElement' will be default for configs with collection === true and won't need behavior
                if (field.collection === true || !isNoU(field.behavior)) {
                    // See below for details on sectionPerElement behavior
                    fieldComponents = fieldComponents.concat(
                        this.makeCollectionManagerWrapper(field, currentValue, fieldPath, resourcePath, sectionKeyPath)
                    );

                    const formProxy = this.makeProxy(fieldPath, resourcePath);

                    // Render footer content below all element sections
                    // Only supported for subsections currently
                    if (field.footerContent && (isNoU(field.showFooterContent) || field.showFooterContent(formProxy, this.props.userGroup) === true )) {
                        fieldComponents.push(
                            <field.footerContent
                                key={`footerContent${this.getUniqueComponentKey(field, resourcePath)}`}
                                formProxy={formProxy}
                                field={field}/>
                        );
                    }
                } else {
                    fieldComponents = fieldComponents.concat(
                        /** Indirect Recursion */
                        this.makeSectionFieldsWithWrapper(field, fieldPath, resourcePath, sectionIndex, sectionKeyPath)
                    );
                }
            } else if (field.conditionalSection === true) {
                // Using field props because conditional section is based on an editable field value not a object that contains editable values
                let fieldProps = this.fieldProps(field, fieldPath);
                // Copy base path because fields of a conditional section are on the same level as the field used for the condition
                let sectionFieldPath = baseFieldPath.slice();
                let key = this.getUniqueComponentKey(field, resourcePath);
                let fields = this.makeSectionFields(field, sectionFieldPath, resourcePath, sectionIndex, sectionKeyPath.slice().concat(key));
                fieldComponents.push(
                    this.makeConditionalSectionWrapper(field, sectionIndex, key, fieldPath, resourcePath, fields, sectionKeyPath)
                );
            } else {
                // Render regular field
                let comp = this.makeComponent(field, fieldPath, resourcePath, sectionIndex, sectionKeyPath, fieldCount);
                if (!isNoU(comp)) {
                    fieldComponents.push(comp);
                }
            }

            fieldCount++;
        }

        return fieldComponents;
    }

    /**
     * Make fields with given config. Wraps in section wrapper if present.
     * @param field
     * @param fieldPath
     * @param resourcePath
     * @param sectionIndex
     * @param sectionKeyPath
     * @return {*} Array of fields or a single component wrapping all fields
     */
    makeSectionFieldsWithWrapper(field, fieldPath, resourcePath, sectionIndex, sectionKeyPath) {
        // let key = this.getUniqueComponentKey(field, resourcePath);
        let key = field.key || fieldPath.join("-")
        console.log(`key`, key)
        // if (key === "steps.0-poll-productPanel") {
        //     debugger;
        // }
        let fields = this.makeSectionFields(field, fieldPath, resourcePath, sectionIndex, sectionKeyPath.slice().concat(key));
        let Wrapper = field.sectionWrapper ? field.sectionWrapper : SectionFragment;
        // if (field.sectionWrapper) {
        //     let SectionWrapper = field.sectionWrapper;
        //
        // }
        return this.makeSectionWrapper(Wrapper, key, field, sectionIndex, fieldPath, resourcePath, fields, resourcePath.join('.'), sectionKeyPath);
        // return (
        //     <React.Fragment>
        //         {fields}
        //     </React.Fragment>
        // );
    }

    /**
     * Renders a section base on the given config 'section'
     * @param section
     * @param index
     * @return {*}
     */
    makeSection(section, index) {
        let SectionWrapper = this.props.sectionWrapper;

        let fieldPath = [];
        let resourcePath = [];

        // Update fieldPath
        // name is optional on sections
        if (section.name) {
            fieldPath.push(section.name);
        }

        // Update resourcePath if this section designates a resource
        if (section.resource) {
            resourcePath.push(section.name);
        }

        //Hide this accordion if it has the showField function and it returns false
        if (section.showField && !section.showField(this.makeProxy(fieldPath, resourcePath))) {
          return {};
        }

        let value = this.currentValueForField(fieldPath);

        if (section.custom) {
            let formProxy = this.makeProxy(fieldPath, resourcePath);
            let key = section.getUniqueSectionKey ? section.getUniqueSectionKey(formProxy) : ((section.key || "") + (section.name || ""));
            return {
                sectionsCreated: 1,
                sectionComp: this.makeSectionWrapper(
                    SectionWrapper,
                    key,
                    section,
                    index,
                    fieldPath,
                    resourcePath,
                    <section.custom formProxy={formProxy}/>,
                    TopLevelSectionName
                )
            };
        }

        // TODO: lots of duplicated logic here with same check above, need to consolidate
        if (section.collection === true) {
            // See below for details on sectionPerElement behavior
            let elementCollection = value;
            let sections = [];

            sections.push(
                this.makeCollectionManagerWrapper(section, elementCollection, fieldPath, resourcePath, [TopLevelSectionName], index, true)
            );

            return {
                sectionsCreated: elementCollection ? elementCollection.length : 0,
                sectionComp: sections
            };
        }

        let key = (section.key || "") + (section.name || "");
        return {
            sectionsCreated: 1,
            sectionComp: this.makeSectionWrapper(
                SectionWrapper,
                key,
                section,
                index,
                fieldPath,
                resourcePath,
                this.makeSectionFields(section, fieldPath, resourcePath, index, [key]),
                TopLevelSectionName
            )
        };
    }

    makeCollectionManagerWrapper(field, collection, fieldPath, resourcePath, sectionKeyPath) {
        return (
            <CollectionManagementSection field={field}
                                         collection={collection}
                                         sectionKeyPath={sectionKeyPath}
                                         formProxy={this.makeProxy(fieldPath, resourcePath)}/>
        );
    }

    makeViewSwitchSectionWrapper(section, viewCaseFields, fieldPath, resourcePath) {
        return (
            <ViewSwitchSection section={section}
                               switchValue={section.viewSwitch(this.makeProxy(fieldPath, resourcePath))}
                               switchCases={viewCaseFields}
                               formProxy={this.makeProxy(fieldPath, resourcePath)} />
        );
    }

    makeConditionalSectionWrapper(field, index, key, fieldPath, resourcePath, fields, sectionGroup) {
        return (
            <ConditionalSection {...field.props}
                                index={index}
                                key={key}
                                sectionKey={key}
                                fieldKey={key}
                                name={field.name}
                                formProxy={this.makeProxy(fieldPath, resourcePath)}
                                value={this.valueForField(fieldPath)}
                                required={field.required}
                                onValueChanged={this.makeOnValueChanged(fieldPath, resourcePath)}
                                conditionalValue={this.currentValueForField(fieldPath) || false}>
                {fields}
            </ConditionalSection>
        );
    }

    makeSectionWrapper(SectionWrapperComponent, key, section, sectionIndex, fieldPath, resourcePath, children) {
        return (
            <SectionWrapperComponent key={key}
                                     index={sectionIndex}
                                     formProxy={this.makeProxy(fieldPath, resourcePath)}
                                     {...section.props}>
                {children}
            </SectionWrapperComponent>
        );
    }

    readyToUpdate() {
        // only update if there is a resource
        return isTruthy(this.props.resource);
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {idPath} = this.props;
        this.resourceDidChange = dotProp.get(nextProps, idPath) !== dotProp.get(this.props, idPath);

        if (this.resourceDidChange) {
            console.log(`resourceDidChange`, dotProp.get(nextProps, idPath), dotProp.get(this.props, idPath))
            return true;
        }

        if (!this.readyToUpdate()) {
            return false;
        }

        if (!this.props.staticRender) {
            return true;
        }

        return !this.state.rendered;
    }

    render() {
        // console.log("render")
        if (this.props.staticRender) {
            if (this.resourceDidChange) {
                this.sections = this.generateSections();
            }

            return this.sections;
        } else {
            return this.generateSections();
        }
    }

    componentDidUpdate() {
        if (this.props.staticRender) {
            this.setState({
                rendered: true
            });
        }
    }
}

/*
 * Resource Form Config Specification
 *
 * The root of the config must consist of a 'section' or a 'fields' property
 * If 'fields' is provided the config is treated as a single section
 *
 * Special Behaviors:
 *
 * sectionPerElement - This behavior will result in the section being render once for each element in an array
 *                     If this is on a top level section, then each section is also wrapped in default section wrapper
 */

const fieldConfig = PropTypes.shape({
    name: PropTypes.string, // Name of property this field is referencing from resource
    props: PropTypes.object, // Static props to pass-through to a field
    type: PropTypes.string, // Field type, must exist in provided 'components' prop of ResourceForm
    subSection: PropTypes.bool, // Weather a sub-section should be started on this field (generally for sub objects)
    collection: PropTypes.bool, // Should collection logic be used for this field
    behavior: PropTypes.object // Special behavior object for collections
});

const sectionConfig = PropTypes.shape({
    key: PropTypes.string,
    props: PropTypes.object, // Static props to pass-through to a section
    fields: PropTypes.arrayOf(fieldConfig) // Fields in this section
});

ResourceForm.propTypes = {
    components: PropTypes.object.isRequired, // Map of types to Components
    onValueChanged: PropTypes.func.isRequired, // Callback for whenever a component's value is edited
    config: PropTypes.shape({ // Config object to build form from
        sections: PropTypes.arrayOf(sectionConfig),
        fields: PropTypes.arrayOf(fieldConfig)
    }).isRequired,
    resource: PropTypes.object.isRequired, // Resource that form will pull data from
    sectionWrapper: PropTypes.func, // Wrapper component that sections are rendered in
    sectionProps: PropTypes.func, // Function that is called to get dynamic props for each section
    fieldProps: PropTypes.func, // Function that is called to get dynamic props for each field
    staticRender: PropTypes.bool, // This form should render only once and pre-generate components

    idPath: PropTypes.string, // The path that should be checked on the resource to determine if a
                              // re-render should happen when staticRender is true.

    errors: PropTypes.object
};

ResourceForm.defaultProps = {
    sectionWrapper: BasicForm,
    sectionProps: EmptyFunc,
    fieldProps: EmptyFunc,
    staticRender: false,
    idPath: 'resource.MASTER.resourceId.id', // specific to Challenge resources
    errors: {}
};

export default ResourceForm;



// WEBPACK FOOTER //
// ./public/components/forms/ResourceForm.jsx